import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { AuthService, CanComponentDeactivate } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config';
import { FormHelper } from '@klickdata/core/form';
import { GlobalSearchData } from '@klickdata/core/global-search';
import { MediaType } from '@klickdata/core/media/src/media-type';
import { MediaService } from '@klickdata/core/media/src/media.service';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { ResourceMediaTypes } from '@klickdata/core/question/src/question-type/question-type';
import {
    AppScope,
    Resource,
    ResourceCategory,
    ResourceService,
    ResourceTag,
    ResourceTypes,
} from '@klickdata/core/resource';
import { ResourceSocketService } from '@klickdata/core/resource/src/resource-ws.service';
import { Utils } from '@klickdata/core/util';
import { CreateResourceBaseDirective } from '@klickdata/shared-components/src/resource-create/create-resource-base.directive';
import { ResourceListingSheetComponent } from '@klickdata/shared-components/src/resource-listing-sheet/resource-listing-sheet.component';
import { ScormManagerCoreComponent } from '@klickdata/shared-components/src/scorm-manager-core/scorm-manager-core.component';
import { TagChipSelectComponent } from '@klickdata/shared-components/src/tag-chip-select/tag-chip-select.component';
import { DialogDisplayImgComponent } from 'apps/klickdata/src/app/shared/dialog/dialog-display-img/dialog-display-img.component';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, filter, first, map, switchMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-course-manager-create-scorm',
    templateUrl: './course-manager-create-scorm.component.html',
    styleUrls: ['./course-manager-create-scorm.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseManagerCreateScormComponent
    extends CreateResourceBaseDirective
    implements OnInit, OnDestroy, CanComponentDeactivate, OnChanges
{
    @ViewChild('compRef') scormManagerCore: ScormManagerCoreComponent;
    @ViewChild(TagChipSelectComponent) tagsComp: TagChipSelectComponent;
    @Output() published: EventEmitter<{ res: Resource; isParentSubmission?: boolean }> = new EventEmitter<{
        res: Resource;
        isParentSubmission?: boolean;
    }>();
    @Output() onError: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() bottomToolSheet: boolean;
    private scormResource: Resource;
    public loadingUploader: Subject<boolean> = new Subject<boolean>();
    public isAIUploadedImg: Subject<boolean> = new Subject<boolean>();
    public scormForm: FormGroup;
    public formats: { [key: string]: string };
    ResourceTypes = ResourceTypes;
    public isAdmin$: Observable<boolean>;
    public defaultResourceGoalCount: number;
    public submitBtnClicked: boolean;
    AppScope = AppScope;
    @ViewChild('coverImagePanel') coverImagePanel: MatExpansionPanel;
    @ViewChild('mainTabs') mainTabs: MatTabGroup;
    public mediaAIPrompterText: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    constructor(
        protected fb: FormBuilder,
        protected auth: AuthService,
        protected route: ActivatedRoute,
        protected messageService: MessageService,
        protected resourceService: ResourceService,
        protected cdRef: ChangeDetectorRef,
        protected bottomSheet: MatBottomSheet,
        protected dialog: MatDialog,
        protected mobile: MobileService,
        protected mediaService: MediaService,
        protected configService: ConfigService,
        protected resourceSocketService: ResourceSocketService
    ) {
        super(messageService, resourceService, cdRef, dialog);
        this.buildForm();
        this.bottomToolSheet = this.bottomToolSheet || this.route.snapshot.data.bottomToolSheet;
        this.defaultResourceGoalCount = this.configService.config.defaultResourceGoalCharCount;
    }

    ngOnInit() {
        this.isAdmin$ = this.auth.getUser().pipe(
            first(),
            map((user) => user.isAdmin())
        );
    }

    public buildForm() {
        this.scormForm = this.fb.group({
            id: [],
            priority: [],
            media_id: [],
            title: ['', Validators.required],
            description: [''],
            bullets: [''],
            instructions: [''],
            tag_ids: [[]],
            category_ids: [[], Validators.required],
            language_id: ['', Validators.required],
            article_code: [''],
        });

        this.formats = {
            title: $localize`:@@title:Title`,
            description: $localize`:@@description:Description`,
            grade_system_id: $localize`:@@gradeSystem:Grade system`,
            categories: $localize`:@@categories:Categories`,
        };
    }
    ngOnChanges() {
        if (this.resource) {
            this.update();
        }
    }
    public update() {
        this.scormForm.patchValue({
            id: this.resource.id,
            priority: this.resource.priority,
            media_id: this.resource.media_id,
            bullets: this.resource.bullets,
            title: this.resource.title,
            instructions: this.resource.instructions,
            description: this.resource.description,
            category_ids: this.resource.category_ids,
            tag_ids: this.resource.tag_ids,
            language_id: this.resource.language_id,
            article_code: this.resource.article_code,
        });
        this.cdRef.markForCheck();
        FormHelper.resetForm(this.scormForm);
    }
    public onSubmit() {
        this.scormManagerCore.onSubmit(true);
    }

    public onSaved(resource: Resource) {
        if (!this.submitBtnClicked) {
            this.updateResource(resource);
            return;
        }
        FormHelper.resetForm(this.scormForm);
        this.messageService.openMessage(MessageSavedComponent);
        this.published.emit({ res: resource, isParentSubmission: false });
        this.submitBtnClicked = false;
    }
    public onSocketResponse(resource: Resource) {
        this.updateResource(resource);
        const tagCtrl = this.scormForm.get('tag_ids');
        const tagsWereDirty = !!tagCtrl?.dirty;
        const localTagIds = (tagCtrl?.value as number[]) ?? [];
        const remoteTagIds = resource.tag_ids ?? [];
        // Keep tags editable while prompts run; merge auto-generated tags with user picks.
        this.scormForm.patchValue({
            title: resource.title,
            tag_ids: tagsWereDirty ? [...new Set([...localTagIds, ...remoteTagIds])] : remoteTagIds,
            media_id: resource.media_id,
        });
        if (tagsWereDirty) {
            tagCtrl?.markAsDirty();
        }
        if (!!resource.prompt_states?.length) {
            const states = this.scormResource.prompt_states;
            const imagePromptState = states.find((state) => state.type === 'image');
            const tagsPromptState = states.find((state) => state.type === 'tags');
            this.loadingUploader.next(
                imagePromptState ? Utils.isOnProgressPromptStatus(imagePromptState?.status) : false
            );
            this.isLoading =
                Utils.isOnProgressPromptStatus(tagsPromptState?.status) ||
                Utils.isOnProgressPromptStatus(imagePromptState?.status);
        }
    }
    private updateResource(resource: Resource) {
        this.scormResource = resource;
        const scormParserPromptState = resource.prompt_states.find((state) => state.type === 'scorm_parser');
        this.isLoading = Utils.isOnProgressPromptStatus(scormParserPromptState.status);
    }
    public showTagResources(tag: ResourceTag) {
        this.showResources({ id: tag.id, label: tag.name, scope_id: AppScope.TAGS });
    }
    public showCatgeoryResources(category: ResourceCategory) {
        this.showResources({ id: category.id, label: category.title, scope_id: AppScope.CATEGORIES });
    }
    private showResources(item: GlobalSearchData) {
        this.bottomSheet.open(ResourceListingSheetComponent, {
            data: {
                id: item.id,
                label: item.label,
                scope_id: item.scope_id,
            },
            panelClass: 'sheet-wrapper',
        });
    }

    public showImgModal(id: number) {
        this.dialog.open(DialogDisplayImgComponent, {
            maxWidth: '70%',
            maxHeight: '50vh',
            data: id,
        });
    }
    public canDeactivate(): boolean {
        return this.scormForm.pristine;
    }

    @HostListener('window:beforeunload', ['$event'])
    handleClose($event) {
        $event.returnValue = this.canDeactivate();
    }

    public addMedia(errorMsg?: string) {
        if (this.scormForm.value.media_id) {
            this.mainTabs.selectedIndex = 1;
            this.coverImagePanel.open();
            return;
        }
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.ADD_RESOURCE_MEDIA,
            data: {
                icon: 'add_photo_alternate',
                title: $localize`Add an image`,
                description: $localize`Please select your preferred way to add an image.`,
                type: ResourceTypes.TEST,
                errorMsg: errorMsg,
            },
        });
        this.getMediaTypeFromSlideIn();
    }
    private getMediaTypeFromSlideIn() {
        this.mobile
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobile.getSideNavResponseData()),
                filter((data) => data.type === ResourceTypes.TEST),
                takeUntil(this.destroy)
            )
            .subscribe((data) => {
                if (data && data.value) {
                    this.scormForm.patchValue({ media_id: data.mediaId });
                    if (data.value === ResourceMediaTypes.FILE) {
                        this.isAIUploadedImg.next(false);
                        this.mediaAIPrompterText.next(null);
                    } else {
                        this.loadingUploader.next(true);
                        this.initAIUpload(data);
                    }
                    this.scormForm.markAsDirty();
                }
            });
    }
    private initAIUpload(data: any) {
        this.mediaService
            .uploadMediaByLink({
                prompt: data.aiText,
                type: MediaType.PROMPT,
                scope_id: ResourceTypes.scopeByChildType(ResourceTypes.TEST),
            })
            .pipe(
                takeUntil(this.destroy),
                catchError((err) => {
                    this.handleAIRequestFailure(err);
                    return of(null);
                })
            )
            .subscribe((media) => {
                this.loadingUploader.next(false);
                if (media && media.id) {
                    this.isAIUploadedImg.next(true);
                    this.scormForm.patchValue({ media_id: media.id });
                    this.mediaAIPrompterText.next(data.aiText);
                }
            });
    }
    handleAIRequestFailure(err) {
        if (err && err.error && err.error.error) {
            this.messageService.openMessage(MessageErrorComponent, err.error.error.messages.join('/n'));
            this.addMedia(err.error.error.messages.join('/n'));
        }
    }
    public ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
